import React, { useEffect, useRef, useState } from 'react';
import { useTutorial } from '@/contexts/TutorialContext';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

type SpotlightPosition = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export const TutorialOverlay: React.FC = () => {
  const navigate = useNavigate();
  const {
    active,
    currentStepIndex,
    currentStep,
    steps,
    skipTutorial,
    nextStep,
    prevStep,
    completeTutorial
  } = useTutorial();

  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Position the spotlight based on the target element
  useEffect(() => {
    if (!active || !currentStep || !currentStep.targetSelector) {
      setSpotlightPosition(null);
      return;
    }

    let retryCount = 0;
    const maxRetries = 5;
    let retryInterval: NodeJS.Timeout;

    const positionSpotlight = () => {
      try {
        const targetElement = document.querySelector(currentStep.targetSelector);
        if (!targetElement) {
          if (retryCount < maxRetries) {
            // Retry a few times before giving up
            retryCount++;
            return;
          }
          setSpotlightPosition(null);
          return;
        }

        // Clear retry interval if we found the element
        if (retryInterval) {
          clearInterval(retryInterval);
        }

        const rect = targetElement.getBoundingClientRect();

        // Check if the element is in viewport and scroll if needed
        const isInViewport = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );

        // Scroll to element if not in viewport or if it's the feedback step
        if (!isInViewport || currentStep.id === 'feedback-overview') {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Small delay to let the scrolling finish before positioning the spotlight
          setTimeout(() => {
            const updatedRect = targetElement.getBoundingClientRect();
            const padding = 8;
            setSpotlightPosition({
              top: updatedRect.top - padding,
              left: updatedRect.left - padding,
              width: updatedRect.width + (padding * 2),
              height: updatedRect.height + (padding * 2)
            });
          }, 500);
          return;
        }

        // Add some padding around the element
        const padding = 8;
        setSpotlightPosition({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + (padding * 2),
          height: rect.height + (padding * 2)
        });
      } catch (error) {
        console.error('Error positioning spotlight:', error);
        setSpotlightPosition(null);
      }
    };

    // Initial positioning
    positionSpotlight();

    // Set up retry interval for finding elements
    retryInterval = setInterval(() => {
      if (retryCount < maxRetries) {
        positionSpotlight();
      } else {
        clearInterval(retryInterval);
      }
    }, 500);

    // Reposition on window resize
    window.addEventListener('resize', positionSpotlight);

    // Cleanup
    return () => {
      window.removeEventListener('resize', positionSpotlight);
      if (retryInterval) {
        clearInterval(retryInterval);
      }
    };
  }, [active, currentStep, currentStepIndex]);

  // Handle transitions between steps
  useEffect(() => {
    if (active) {
      setIsTransitioning(false);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [currentStepIndex, active]);

  if (!active || !currentStep) return null;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        pointerEvents: 'none',
        transition: 'background-color 0.3s ease'
      }}
    >
      {/* Semi-transparent overlay that blocks interactions with non-spotlighted areas */}
      <div className="absolute inset-0" style={{ pointerEvents: 'auto' }}></div>

      {/* Spotlight - this creates a "hole" in the overlay */}
      {spotlightPosition && (
        <div
          className="absolute"
          style={{
            top: spotlightPosition.top,
            left: spotlightPosition.left,
            width: spotlightPosition.width,
            height: spotlightPosition.height,
            background: 'transparent',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
            borderRadius: '4px',
            transition: 'all 0.3s ease',
            // Important: make the spotlight area allow interactions to pass through
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Tooltip - with sticky positioning for better visibility */}
      <div
        className={cn(
          "bg-white rounded-lg p-6 z-[100] sticky top-4 flex flex-col gap-4 border border-stone-200 shadow-xl dark:bg-stone-800 dark:border-stone-700",
          isTransitioning ? "opacity-0" : "opacity-100",
        )}
        style={{
          width: '420px',
          maxWidth: '90vw',
          margin: '0 auto', // Center horizontally
          transition: 'opacity 0.3s ease',
          pointerEvents: 'auto' // Make the tooltip interactive
        }}>

        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold dark:text-white">
            {currentStep.title}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={skipTutorial}
            aria-label="Close tutorial"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-stone-600 dark:text-stone-300">
          {currentStep.content}
        </p>

        {currentStep.action && (
          <div className="p-2 bg-orange-50 dark:bg-stone-700 rounded border-l-4 border-orange-500 text-sm text-stone-700 dark:text-stone-300">
            <strong>Action:</strong> {currentStep.action}
            {currentStep.requiredAction && (
              <Button
                variant="link"
                size="sm"
                onClick={nextStep}
                className="ml-2 text-orange-600 dark:text-orange-400 p-0 h-auto"
              >
                (Skip this action)
              </Button>
            )}
          </div>
        )}

        {/* Progress dots */}
        <div className="flex justify-center space-x-1">
          {steps.map((_, index) => (
            <div
              key={index}
              className={cn(
                "h-2 w-2 rounded-full transition-colors",
                index === currentStepIndex
                  ? "bg-orange-500"
                  : "bg-stone-300 dark:bg-stone-600"
              )}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex flex-col space-y-3">
          <div className="flex justify-between items-center">
            <div>
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  size="sm"
                  className="h-9"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
              )}
            </div>

            <div>
              {!isLastStep ? (
                <Button
                  onClick={nextStep}
                  size="sm"
                  className="h-9 bg-orange-500 hover:bg-orange-600"
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={completeTutorial}
                  size="sm"
                  className="h-9 bg-orange-500 hover:bg-orange-600"
                >
                  Finish
                  <CheckCircle className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};
